import Arrow from '@/assets/icons/chevron-right.svg?react';
import ArrowLeft from '@/assets/icons/chevron-left.svg?react';
import SortArrows from '@/assets/icons/chevron-selector-vertical.svg?react';
import ChevronUp from '@/assets/icons/chevron-up.svg?react';
import ChevronDown from '@/assets/icons/chevron-down.svg?react';
import { Empty, Flex, Spin } from 'antd';
import { generateDataSource } from './documents-table.utils';
import { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { Document } from '@/services/documents-service/documents-service.types';
import { Pagination } from '@/services/request/request.types';
import { OrderBy } from '@/hooks/use-sort-and-pagination/use-sort-and-pagination.types';
import { PAGE_SIZE_OPTIONS } from '@/hooks/use-sort-and-pagination/use-sort-and-pagination';
import Icon, { LoadingOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import styles from './documents-table.module.scss';
import { calculateDateFromISOtoYYYY_MM_DD, calculateHourFromISODate } from '@/utils/helpers';
import { FilterValue, SorterResult, SortOrder } from 'antd/es/table/interface';
import { DetailsLink } from '../details-link/details-link';
import VsTable from '../vs-table/vs-table';
import { useTranslation } from 'react-i18next';

interface DocumentsTableProps {
  onChange: (page?: number, perPage?: number, sortBy?: string, orderBy?: OrderBy) => void;
  documentsData?: Document[];
  pagination?: Pagination;
  isAdmin?: boolean;
  currentPage: number;
  pageSize: number;
  loading?: boolean;
  sortBy?: string;
  orderBy?: OrderBy;
}

export const DocumentsTable = ({
  onChange,
  documentsData,
  pagination,
  isAdmin,
  currentPage,
  pageSize,
  loading,
  sortBy,
  orderBy,
}: DocumentsTableProps) => {
  const { t } = useTranslation(['documents', 'common']);
  const dataSource = generateDataSource(documentsData || []);

  const sortGlyph = ({ sortOrder }: { sortOrder: SortOrder }) => {
    const Glyph =
      sortOrder === 'ascend' ? ChevronUp : sortOrder === 'descend' ? ChevronDown : SortArrows;

    return (
      <Icon component={() => <Glyph stroke={styles.arrowColorIcon} width={14} height={14} />} />
    );
  };

  // created_at loads sorted desc, so it starts its cycle there and the first click moves to asc.
  const sortDirectionsFor = (field: string): SortOrder[] =>
    field === 'created_at' ? ['descend', 'ascend'] : ['ascend', 'descend'];

  const orderOf = (direction: SortOrder): OrderBy => (direction === 'ascend' ? 'asc' : 'desc');

  const sortProps = (field: string) => ({
    sorter: true,
    sortDirections: sortDirectionsFor(field),
    sortOrder:
      sortBy === field ? (orderBy === 'asc' ? ('ascend' as const) : ('descend' as const)) : null,
    sortIcon: sortGlyph,
  });

  const handleChange = (
    pagination: TablePaginationConfig,
    _: Record<string, FilterValue | null>,
    sorter: SorterResult<Document> | SorterResult<Document>[]
  ) => {
    if (!Array.isArray(sorter)) {
      sorter = [sorter];
    }

    const sort = sorter.find((s) => s.order);

    let nextSortBy: string | undefined;
    let nextOrderBy: OrderBy | undefined;

    if (sort?.order) {
      nextSortBy = sort.field?.toString();
      nextOrderBy = orderOf(sort.order);
    } else if (sortBy) {
      // antd appends a cancel step to every cycle, and reports it with no field, so the cancelled
      // column is whichever one is sorted. Restart it rather than report no sort, which the page
      // coerces to its default and moves the arrow to another header.
      nextSortBy = sortBy;
      nextOrderBy = orderOf(sortDirectionsFor(sortBy)[0]);
    }

    onChange(pagination.current, pagination.pageSize, nextSortBy, nextOrderBy);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const columns: ColumnsType<Document> = [
    {
      title: t('documents:table.columns.document'),
      dataIndex: 'name',
      key: 'name',
      className: styles.nameContainer,
      ...sortProps('name'),
      render: (name: Document['name']) => (!name ? <p>---</p> : <div>{name}</div>),
    },
    {
      title: t('documents:table.columns.content'),
      dataIndex: 'content',
      key: 'content',
      className: styles.bodyContainer,
      render: (content: Document['content']) =>
        !content ? (
          <p>---</p>
        ) : (
          <div>{content.length > 220 ? `${content.slice(0, 220)}...` : content}</div>
        ),
    },
    {
      title: t('documents:table.columns.id'),
      dataIndex: 'id',
      key: 'id',
      className: styles.idContainer,
      ...sortProps('id'),
      render: (id: Document['id']) => <div>{id}</div>,
    },
    {
      title: t('documents:table.columns.phoneNumber'),
      dataIndex: 'phone',
      key: 'phone',
      className: styles.phoneNumberContainer,
      render: (phone: Document['phone'], record) => {
        return (
          <>
            {phone ? (
              <Link to={`/documents/${record.id}`} className={styles.phoneNumber}>
                {phone}
              </Link>
            ) : (
              t('common:states.none')
            )}
          </>
        );
      },
    },

    ...(isAdmin
      ? [
          {
            title: t('documents:table.columns.company'),
            dataIndex: 'company_name',
            key: 'company',
            className: styles.companyContainer,
            render: (company_name: Document['company_name']) => <div>{company_name || ''}</div>,
          },
        ]
      : []),

    {
      title: t('documents:table.columns.created'),
      dataIndex: 'created_at',
      key: 'created_at',
      className: styles.dateAndTimeContainer,
      ...sortProps('created_at'),
      render: (created_at: Document['created_at']) => (
        <Flex vertical>
          <Flex wrap="wrap">
            <p className={styles.date}>{calculateDateFromISOtoYYYY_MM_DD(created_at)}</p>
            <span className={styles.circle}>&#9679;</span>
            <p className={styles.hour}>{calculateHourFromISODate(created_at)}</p>
          </Flex>
        </Flex>
      ),
    },
    {
      title: t('documents:table.columns.updated'),
      dataIndex: 'updated_at',
      key: 'updated_at',
      className: styles.dateAndTimeContainer,
      render: (updated_at: Document['updated_at']) => (
        <Flex vertical>
          <Flex wrap="wrap">
            <p className={styles.date}>{calculateDateFromISOtoYYYY_MM_DD(updated_at)}</p>
            <span className={styles.circle}>&#9679;</span>
            <p className={styles.hour}>{calculateHourFromISODate(updated_at)}</p>
          </Flex>
        </Flex>
      ),
    },

    {
      className: styles.actionContainer,
      render: (record) => <DetailsLink link={`/documents/${record.id}`} />,
    },
  ];

  return (
    // VsTable defaults pagination off, so the numbered pager is passed back in.
    <VsTable<Document>
      pagination={{
        current: currentPage,
        position: ['bottomCenter'],
        pageSizeOptions: PAGE_SIZE_OPTIONS.map(String),
        showSizeChanger: true,
        // Controlled: uncontrolled, antd reports no page size on a sort change and the page
        // falls back to its default, silently dropping a deep-linked items_per_page.
        pageSize,
        total: pagination?.totalCount,
        locale: { items_per_page: '' },
        nextIcon: <Icon component={() => <Arrow strokeWidth={1} width={16} height={16} />} />,
        prevIcon: <Icon component={() => <ArrowLeft strokeWidth={2} width={16} height={16} />} />,
      }}
      onChange={handleChange}
      scroll={{ x: 800 }}
      className={styles.table}
      dataSource={dataSource}
      columns={columns}
      locale={{
        emptyText: loading ? (
          <div className={styles.emptyState}>
            <Spin indicator={<LoadingOutlined spin />} />
          </div>
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ),
      }}
    />
  );
};
